import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Check,
  ChevronDown,
  LoaderCircle,
  LogIn,
  LogOut,
  LockKeyhole,
  Send,
  Sparkles,
  Upload,
  Utensils,
  X,
} from "lucide-react";
import PaymentWidget from "./PaymentWidget";

const DEFAULT_CONFIG = {
  joinEnabled: false,
  leaveEnabled: false,
  joinChannelId: "",
  leaveChannelId: "",
  joinMessage:
    "{mention}님, {server}에 오신 걸 환영해요! 현재 {member_count}명이 함께하고 있어요.",
  leaveMessage: "{user}님이 서버를 떠났어요. 다음에 또 만나요!",
  joinMention: true,
  leaveMention: false,
  format: "text",
  imageSource: "avatar",
  customImage: "",
  background: "random",
  customBackground: "",
  showUserId: true,
  showDiscordJoined: true,
  showServerJoined: true,
  cardTitle: "반가워요, {user}님!",
  cardText: "",
  leaveCardTitle: "안녕히 가세요, {user}님!",
  leaveCardText: "",
  cardTextSize: 35,
  cardAiEnabled: false,
  cardAiPrompt: "",
  leaveCardAiEnabled: false,
  leaveCardAiPrompt: "",
  font: "pretendard",
  aiEnabled: false,
  aiPrompt:
    "새 멤버를 친근하게 환영하고, 규칙 채널을 확인해 달라고 디덕이 말투로 안내해 줘.",
};

const BACKGROUNDS = [
  { value: "random", label: "매번 랜덤", image: "/welcome-assets/jungle.png" },
  { value: "jungle", label: "정글", image: "/welcome-assets/jungle.png" },
  { value: "savanna", label: "사바나", image: "/welcome-assets/savanna.png" },
  { value: "beach", label: "해변", image: "/welcome-assets/beach.png" },
];

const FONTS = [
  { value: "pretendard", label: "프리텐다드" },
  { value: "maple", label: "메이플스토리" },
];

const FONT_FAMILY = {
  pretendard: "Pretendard, sans-serif",
  maple: "MapleStory, sans-serif",
};
const DEFAULT_DISCORD_AVATAR = "https://cdn.discordapp.com/embed/avatars/0.png";

async function optimizeBackground(file) {
  if (!file.type.startsWith("image/") || file.size > 1.45 * 1024 * 1024)
    throw new Error("1.45MB 이하 이미지를 올려 주세요.");
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error("이미지를 읽지 못했습니다."));
      image.src = url;
    });
    if (image.naturalWidth * image.naturalHeight > 12000000)
      throw new Error("이미지 해상도가 너무 큽니다.");
    const data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return { data, width: image.naturalWidth, height: image.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function Dropdown({ value, options, onChange, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (event) =>
      ref.current && !ref.current.contains(event.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const selected = options.find((option) => option.value === value);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-[#404249] bg-[#232428] px-4 text-left text-sm text-white transition-colors hover:border-[#5b5e68] focus:border-[#5865F2] focus:outline-none"
      >
        <span className="truncate">{selected?.label || label}</span>
        <ChevronDown
          className={`h-4 w-4 flex-none text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-[#3b3d44] bg-[#202125] p-1 shadow-2xl"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`block w-full rounded-md px-3 py-2.5 text-left text-sm ${option.value === value ? "bg-[#5865F2]/20 text-white" : "text-gray-300 hover:bg-[#2b2d32]"}`}
              >
                {option.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-200">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-[#5865F2]" : "bg-[#3a3c43]"}`}
      >
        <span
          className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-transform ${checked ? "left-6" : "left-1"}`}
        />
      </button>
      {label}
    </label>
  );
}

function EventEditor({
  title,
  enabled,
  setEnabled,
  channelId,
  setChannelId,
  message,
  setMessage,
  mention,
  setMention,
  channels,
}) {
  return (
    <div
      className={`rounded-lg border p-4 transition-colors ${enabled ? "border-[#5865F2]/45 bg-[#5865F2]/5" : "border-[#34363c] bg-[#202125]"}`}
    >
      <div className="flex items-center justify-between gap-3">
        <h4 className="font-bold text-white">{title}</h4>
        <Toggle
          checked={enabled}
          onChange={setEnabled}
          label={enabled ? "사용" : "꺼짐"}
        />
      </div>
      {enabled && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 space-y-4"
        >
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-400">
              메시지를 보낼 채널
            </label>
            <Dropdown
              value={channelId}
              onChange={setChannelId}
              options={channels}
              label="텍스트 채널 선택"
            />
          </div>
          <div>
            <div className="mb-1.5 flex justify-between">
              <label className="text-xs font-semibold text-gray-400">
                메시지
              </label>
              <span className="text-xs text-gray-600">
                {message.length}/1000
              </span>
            </div>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={1000}
              rows={4}
              className="w-full resize-y rounded-lg border border-[#404249] bg-[#232428] px-4 py-3 text-sm leading-6 text-white outline-none placeholder:text-gray-600 focus:border-[#5865F2]"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Toggle
              checked={mention}
              onChange={setMention}
              label="사용자 멘션하기"
            />
            <span className="text-xs text-gray-500">
              {"{mention} {user} {server} {member_count}"}
            </span>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function FoodModal({
  serverId,
  customerName,
  onClose,
  onPurchased,
  setNotice,
  localPreview,
}) {
  const [details, setDetails] = useState(false);
  const [terms, setTerms] = useState(false);
  const [supply, setSupply] = useState(false);
  const [creating, setCreating] = useState(false);
  const [order, setOrder] = useState(null);
  const buy = async () => {
    if (!terms || !supply || creating) return;
    if (localPreview) {
      onPurchased();
      onClose();
      setNotice({
        type: "success",
        message:
          "로컬 미리보기에서 먹이 구매 후 화면을 열었습니다. 실제 결제는 발생하지 않았습니다.",
      });
      return;
    }
    setCreating(true);
    try {
      const response = await fetch("https://disparty.com/api/payments/orders", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: "dideok-food",
          serverId,
          locale: "ko",
          purchaseTermsAccepted: true,
          digitalSupplyConsent: true,
          intermediaryNoticeAccepted: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.message || "주문을 만들지 못했습니다.");
      setOrder(data);
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setCreating(false);
    }
  };
  return createPortal(
    <motion.div
      className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        className="relative max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-lg border border-[#5058c9] bg-[#1b1c20] shadow-[0_25px_80px_rgba(0,0,0,.65)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/35 text-white hover:bg-black/55"
          aria-label="닫기"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="relative h-44 overflow-hidden bg-[#2b2440]">
          <img
            src="/welcome-assets/food-modal-jungle.png"
            alt=""
            className="h-full w-full object-cover opacity-70"
          />
          <img
            src="/dideok-mascot.png"
            alt="디덕이"
            className="absolute bottom-[-30px] right-6 h-44 w-44 object-contain drop-shadow-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1b1c20] via-[#1b1c20]/55 to-transparent" />
          <div className="absolute left-6 top-8">
            <span className="text-xs font-bold text-[#aeb4ff]">
              PARTYBOT BOOST
            </span>
            <h3 className="mt-2 text-2xl font-black text-white">디덕이 먹이</h3>
            <p className="mt-1 text-xl font-black text-[#8f97ff]">500원</p>
          </div>
        </div>
        <div className="space-y-4 p-6">
          <p className="text-sm leading-6 text-gray-200">
            디덕이에게 먹이를 주면 너무 기뻐서 서버에 도움을 줘요!
          </p>
          <button
            type="button"
            onClick={() => setDetails((value) => !value)}
            className="inline-flex items-center gap-1 text-sm font-bold text-[#9ea6ff] hover:text-white"
            aria-expanded={details}
          >
            {details ? "설명 접기" : "자세히 알아보기"}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${details ? "rotate-180" : ""}`}
            />
          </button>
          {details && (
            <ul className="space-y-2 rounded-lg border border-[#34363d] bg-[#202125] p-4 text-sm text-gray-300">
              <li>AI가 새 멤버마다 다른 입장문을 작성해요.</li>
              <li>내가 만든 이미지를 환영 카드 배경으로 올릴 수 있어요.</li>
              <li>
                한 번 구매하면 내 파티봇 서버 설정에서 계속 사용할 수 있어요.
              </li>
            </ul>
          )}
          {!order ? (
            <>
              <label className="flex gap-3 text-sm leading-5 text-gray-300">
                <input
                  type="checkbox"
                  checked={terms}
                  onChange={(event) => setTerms(event.target.checked)}
                  className="mt-1 accent-[#5865F2]"
                />
                <span>
                  구매 조건과 환불 정책, 디스파티가 직접 판매하는 디지털
                  상품임을 확인합니다.
                </span>
              </label>
              <label className="flex gap-3 text-sm leading-5 text-gray-300">
                <input
                  type="checkbox"
                  checked={supply}
                  onChange={(event) => setSupply(event.target.checked)}
                  className="mt-1 accent-[#5865F2]"
                />
                <span>
                  결제 직후 기능이 계정에 공급되며 사용 후 단순 변심 철회가
                  제한될 수 있음에 동의합니다.
                </span>
              </label>
              <button
                type="button"
                onClick={buy}
                disabled={!terms || !supply || creating}
                className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] font-bold text-white hover:bg-[#4752c4] disabled:cursor-not-allowed disabled:bg-[#3a3c4b] disabled:text-gray-500"
              >
                {creating ? (
                  <LoaderCircle className="h-5 w-5 animate-spin" />
                ) : (
                  <Utensils className="h-5 w-5" />
                )}
                먹이 사러 가기
              </button>
            </>
          ) : (
            <PaymentWidget
              {...order}
              price={order.amount}
              customerName={customerName}
              successPath="/ko?tab=partybot"
              failPath="/ko?tab=partybot"
              onFail={(error) =>
                setNotice({ type: "error", message: error.message })
              }
            />
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

export default function PartyBotWelcomeSettings({
  selectedServerId,
  selectedServer,
  channels,
  initialConfig,
  foodOwned,
  onFoodOwned,
  onSaved,
  setNotice,
}) {
  const reduceMotion = useReducedMotion();
  const [form, setForm] = useState(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [foodOpen, setFoodOpen] = useState(false);
  const [cardEvent, setCardEvent] = useState("join");
  const [cardBodyMode, setCardBodyModeState] = useState("metadata");
  const localPreview =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const set = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  useEffect(() => {
    setForm({
      ...DEFAULT_CONFIG,
      ...(initialConfig || {}),
      joinChannelId: initialConfig?.joinChannelId || channels[0]?.value || "",
      leaveChannelId: initialConfig?.leaveChannelId || channels[0]?.value || "",
    });
    setCardBodyModeState(
      initialConfig?.cardAiEnabled
        ? "ai"
        : initialConfig?.cardText
          ? "direct"
          : "metadata",
    );
  }, [initialConfig, channels]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("checkout") !== "success" ||
      !params.get("paymentKey") ||
      !params.get("orderId")
    )
      return;
    fetch("https://disparty.com/api/payments/verify", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        paymentKey: params.get("paymentKey"),
        orderId: params.get("orderId"),
        amount: Number(params.get("amount")),
      }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok)
          throw new Error(data.message || "결제를 확인하지 못했습니다.");
        onFoodOwned();
        setNotice({
          type: "success",
          message: "디덕이가 먹이를 맛있게 먹었습니다! 고급 기능이 열렸어요.",
        });
      })
      .catch((error) => setNotice({ type: "error", message: error.message }))
      .finally(() => {
        const url = new URL(window.location.href);
        [
          "checkout",
          "paymentKey",
          "orderId",
          "amount",
          "code",
          "message",
        ].forEach((key) => url.searchParams.delete(key));
        window.history.replaceState({}, "", url);
      });
  }, [onFoodOwned, setNotice]);

  const upload = async (file) => {
    if (!file) return;
    setOptimizing(true);
    try {
      const image = await optimizeBackground(file);
      setForm((current) => ({
        ...current,
        customBackground: image.data,
        background: "custom",
      }));
      setNotice({
        type: "success",
        message: "이미지를 준비했습니다. 저장할 때 무손실 압축됩니다.",
      });
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setOptimizing(false);
    }
  };
  const uploadRepresentative = async (file) => {
    if (!file) return;
    if (!foodOwned) {
      setFoodOpen(true);
      return;
    }
    setOptimizing(true);
    try {
      const image = await optimizeBackground(file);
      setForm((current) => ({
        ...current,
        customImage: image.data,
        imageSource: "custom",
      }));
      const ratio = image.width / image.height;
      setNotice(
        Math.abs(ratio - 16 / 9) > 0.08
          ? {
              type: "error",
              message: `현재 ${image.width}×${image.height}px입니다. 16:9가 아니어서 일부가 잘릴 수 있습니다.`,
            }
          : {
              type: "success",
              message:
                "대표 이미지를 준비했습니다. 저장할 때 무손실 압축됩니다.",
            },
      );
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setOptimizing(false);
    }
  };
  const save = async () => {
    if (saving) return false;
    if (localPreview) {
      setNotice({
        type: "success",
        message: "로컬 미리보기에서 입장 및 퇴장 설정을 확인했습니다.",
      });
      return true;
    }
    setSaving(true);
    try {
      const response = await fetch(
        "https://disparty.com/api/bot/welcome-config",
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ guildId: selectedServerId, ...form }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.message || "설정을 저장하지 못했습니다.");
      onSaved?.(form);
      setNotice({ type: "success", message: data.message });
      return true;
    } catch (error) {
      setNotice({ type: "error", message: error.message });
      return false;
    } finally {
      setSaving(false);
    }
  };
  const sendTest = async () => {
    if (testing || !(await save())) return;
    if (localPreview) {
      setNotice({ type: "success", message: "운영 환경에서는 선택한 Discord 채널로 테스트 메시지가 전송됩니다." });
      return;
    }
    setTesting(true);
    try {
      const response = await fetch("https://disparty.com/api/bot/welcome-test", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guildId: selectedServerId, event: cardEvent }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "테스트 메시지를 보내지 못했습니다.");
      setNotice({ type: "success", message: data.message });
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setTesting(false);
    }
  };
  const selectedBackground =
    form.background === "custom" && form.customBackground
      ? form.customBackground
      : BACKGROUNDS.find((item) => item.value === form.background)?.image ||
        BACKGROUNDS[0].image;
  const selectedRepresentative =
    form.imageSource === "custom" && form.customImage
      ? form.customImage
      : form.imageSource === "dideok"
        ? "/dideok-mascot.png"
        : DEFAULT_DISCORD_AVATAR;
  const leaveCard = cardEvent === "leave";
  const activeMessage = leaveCard ? form.leaveMessage : form.joinMessage;
  const previewUser = leaveCard ? "퇴장 멤버" : "새 멤버";
  const cardTitleKey = leaveCard ? "leaveCardTitle" : "cardTitle";
  const cardTextKey = leaveCard ? "leaveCardText" : "cardText";
  const cardAiEnabledKey = leaveCard
    ? "leaveCardAiEnabled"
    : "cardAiEnabled";
  const cardAiPromptKey = leaveCard ? "leaveCardAiPrompt" : "cardAiPrompt";
  const previewCardTitle = form[cardTitleKey]
    .replaceAll("{mention}", previewUser)
    .replaceAll("{user}", previewUser)
    .replaceAll("{server}", selectedServer?.server_name || "서버")
    .replaceAll("{member_count}", "128");
  const previewCardText = (
    form[cardAiEnabledKey]
      ? leaveCard
        ? "AI가 떠나는 멤버에게 어울리는 작별 문구를 이곳에 작성해요."
        : "AI가 새 멤버에게 어울리는 환영 문구를 이곳에 작성해요."
      : form[cardTextKey]
  )
    .replaceAll("{mention}", previewUser)
    .replaceAll("{user}", previewUser)
    .replaceAll("{server}", selectedServer?.server_name || "서버")
    .replaceAll("{member_count}", "128");
  const setCardBodyMode = (mode) => {
    const nextMode = cardBodyMode === mode ? "metadata" : mode;
    setCardBodyModeState(nextMode);
    setForm((current) => ({
      ...current,
      [cardTextKey]: nextMode === "metadata" ? "" : current[cardTextKey],
      [cardAiEnabledKey]: nextMode === "ai",
      showUserId: nextMode === "metadata",
      showDiscordJoined: nextMode === "metadata",
      showServerJoined: nextMode === "metadata",
    }));
  };
  const switchCardEvent = (event) => {
    setCardEvent(event);
    const isLeave = event === "leave";
    const text = isLeave ? form.leaveCardText : form.cardText;
    const ai = isLeave ? form.leaveCardAiEnabled : form.cardAiEnabled;
    setCardBodyModeState(ai ? "ai" : text ? "direct" : "metadata");
  };
  const openFoodForLockedControl = (event) => {
    if (
      !foodOwned &&
      event.target.closest("label")?.textContent.includes("내 배경 올리기")
    ) {
      event.preventDefault();
      setFoodOpen(true);
    }
  };

  return (
    <motion.div
      key="welcome"
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onClickCapture={openFoodForLockedControl}
      className="partybot-welcome grid xl:grid-cols-[minmax(0,1fr)_330px]"
    >
      <div className="space-y-5 p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-white">
              입장 & 퇴장 메시지
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              필요한 알림만 켜고, 서로 다른 채널과 문구를 지정할 수 있습니다.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFoodOpen(true)}
            className={`inline-flex min-h-10 items-center gap-2 rounded-lg border px-4 text-sm font-bold transition-colors ${foodOwned ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200" : "border-[#6d63d8] bg-[#5865F2]/15 text-[#c8ccff] hover:bg-[#5865F2]/25"}`}
          >
            {foodOwned ? (
              <Check className="h-4 w-4" />
            ) : (
              <Utensils className="h-4 w-4" />
            )}
            {foodOwned ? "먹이 줌" : "디덕이한테 먹이 주기"}
          </button>
        </div>
        <section>
          <p className="mb-2 text-xs font-bold text-gray-500">1. 알림 선택</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              ["join", "입장", form.joinEnabled, LogIn],
              ["leave", "퇴장", form.leaveEnabled, LogOut],
            ].map(([value, label, enabled, Icon]) => (
              <button
                key={value}
                type="button"
                onClick={() => switchCardEvent(value)}
                className={`flex min-h-14 items-center gap-3 rounded-lg border px-4 text-left transition-colors active:scale-[.98] ${cardEvent === value ? "border-[#7c86ff] bg-[#5865F2]/15 text-white" : "border-[#3b3d44] bg-[#202125] text-gray-400 hover:border-[#50525b]"}`}
              >
                <Icon className="h-5 w-5 flex-none" />
                <span className="min-w-0 flex-1 font-bold">{label} 메시지</span>
                <span className={`text-xs font-semibold ${enabled ? "text-[#aeb4ff]" : "text-gray-600"}`}>
                  {enabled ? "사용 중" : "꺼짐"}
                </span>
              </button>
            ))}
          </div>
        </section>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={cardEvent}
            initial={reduceMotion ? false : { opacity: 0, x: cardEvent === "join" ? -8 : 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, x: cardEvent === "join" ? 8 : -8 }}
            transition={{ duration: 0.18 }}
          >
            <EventEditor
              title={leaveCard ? "퇴장 안내" : "입장 환영"}
              enabled={leaveCard ? form.leaveEnabled : form.joinEnabled}
              setEnabled={(value) => set(leaveCard ? "leaveEnabled" : "joinEnabled", value)}
              channelId={leaveCard ? form.leaveChannelId : form.joinChannelId}
              setChannelId={(value) => set(leaveCard ? "leaveChannelId" : "joinChannelId", value)}
              message={activeMessage}
              setMessage={(value) => set(leaveCard ? "leaveMessage" : "joinMessage", value)}
              mention={leaveCard ? form.leaveMention : form.joinMention}
              setMention={(value) => set(leaveCard ? "leaveMention" : "joinMention", value)}
              channels={channels}
            />
          </motion.div>
        </AnimatePresence>
        <div>
          <p className="mb-2 text-xs font-bold text-gray-500">2. 메시지 꾸미기</p>
          <label className="mb-2 block text-xs font-semibold text-gray-400">
            메시지 모양
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              ["text", "일반 메시지"],
              ["embed", "글 + 이미지"],
              ["card", "정보 카드"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  set("format", value);
                  if (value === "card") set("aiEnabled", false);
                }}
                className={`min-h-11 rounded-lg border px-3 text-sm font-bold transition-colors ${form.format === value ? "border-[#7c86ff] bg-[#5865F2]/15 text-white" : "border-[#3b3d44] bg-[#232428] text-gray-400 hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {form.format !== "text" && (
          <details className="group rounded-lg border border-[#34363c] bg-[#202125] p-4">
            <summary className="cursor-pointer list-none text-sm font-bold text-gray-200 marker:hidden">
              대표 이미지 설정
              <ChevronDown className="float-right h-4 w-4 text-gray-500 transition-transform group-open:rotate-180" />
            </summary>
            <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {[
                ["avatar", "새 멤버 프로필"],
                ["dideok", "디덕이"],
                ["custom", "내 이미지"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() =>
                    value === "custom" && !foodOwned
                      ? setFoodOpen(true)
                      : set("imageSource", value)
                  }
                  className={`rounded-lg border p-3 text-sm font-bold ${form.imageSource === value ? "border-[#7c86ff] bg-[#5865F2]/15 text-white" : "border-[#3b3d44] text-gray-400"}`}
                >
                  {label}
                  {value === "custom" && !foodOwned && (
                    <LockKeyhole className="ml-1 inline h-3 w-3" />
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs leading-5 text-gray-500">
              권장 크기 1200×675px (16:9), 최대 1.45MB · 다른 비율은 출력할 때
              일부가 잘릴 수 있습니다.
            </p>
            <label
              onClick={(event) => {
                if (!foodOwned) {
                  event.preventDefault();
                  setFoodOpen(true);
                }
              }}
              className={`flex min-h-11 items-center justify-center gap-2 rounded-lg border border-dashed px-4 text-sm font-bold ${foodOwned && !optimizing ? "cursor-pointer border-[#656dc8] text-[#b9beff]" : "cursor-pointer border-[#3b3d44] text-gray-600"}`}
            >
              {optimizing ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {optimizing ? "이미지 준비 중" : "대표 이미지 올리기"}
              {!foodOwned && <LockKeyhole className="h-3.5 w-3.5" />}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={!foodOwned || optimizing}
                onChange={(event) =>
                  uploadRepresentative(event.target.files?.[0])
                }
                className="sr-only"
              />
            </label>
            </div>
          </details>
        )}
        {form.format === "card" && (
          <div className="space-y-4 rounded-lg border border-[#34363c] bg-[#202125] p-4">
            <div>
              <label className="mb-2 block text-xs font-semibold text-gray-400">
                카드 배경
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {BACKGROUNDS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => set("background", item.value)}
                    className={`overflow-hidden rounded-lg border text-left ${form.background === item.value ? "border-[#7c86ff]" : "border-[#3b3d44]"}`}
                  >
                    <img
                      src={item.image}
                      alt=""
                      className="aspect-video w-full object-cover"
                    />
                    <span className="block px-2 py-2 text-xs font-bold text-gray-200">
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs leading-5 text-gray-500">
              권장 크기 1200×675px (16:9), 최대 1.45MB · 다른 비율은
              가장자리가 잘릴 수 있습니다.
            </p>
            <label
              className={`flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-4 text-sm font-bold ${foodOwned && !optimizing ? "border-[#656dc8] text-[#b9beff]" : "cursor-not-allowed border-[#3b3d44] text-gray-600"}`}
            >
              {optimizing ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {optimizing ? "이미지 최적화 중" : "내 배경 올리기"}{" "}
              {!foodOwned && <LockKeyhole className="h-3.5 w-3.5" />}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={!foodOwned || optimizing}
                onChange={(event) => upload(event.target.files?.[0])}
                className="sr-only"
              />
            </label>
            <div>
              <label className="mb-2 block text-xs font-semibold text-gray-400">
                카드 제목
              </label>
              <input
                value={form[cardTitleKey]}
                onChange={(event) => set(cardTitleKey, event.target.value)}
                maxLength={80}
                className="mb-4 min-h-11 w-full rounded-lg border border-[#404249] bg-[#232428] px-4 text-sm text-white outline-none placeholder:text-gray-600 focus:border-[#5865F2]"
                placeholder={
                  leaveCard
                    ? "예: 안녕히 가세요, {user}님!"
                    : "예: 반가워요, {user}님!"
                }
              />
              <label className="mb-2 block text-xs font-semibold text-gray-400">
                {leaveCard ? "퇴장 카드 본문" : "입장 카드 본문"}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["direct", "직접 글쓰기"],
                  ["ai", "AI로 만들기"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      value === "ai" && !foodOwned
                        ? setFoodOpen(true)
                        : setCardBodyMode(value)
                    }
                    className={`min-h-10 rounded-lg border px-2 text-sm font-bold ${cardBodyMode === value ? "border-[#7c86ff] bg-[#5865F2]/15 text-white" : "border-[#3b3d44] bg-[#232428] text-gray-400 hover:text-white"}`}
                  >
                    {label}
                    {value === "ai" && !foodOwned && (
                      <LockKeyhole className="ml-1 inline h-3 w-3" />
                    )}
                  </button>
                ))}
              </div>
              {cardBodyMode === "direct" && (
                <div className="mt-3">
                  <textarea
                    value={form[cardTextKey]}
                    onChange={(event) => set(cardTextKey, event.target.value)}
                    maxLength={180}
                    rows={3}
                    className="w-full resize-y rounded-lg border border-[#404249] bg-[#232428] px-4 py-3 text-sm leading-6 text-white outline-none focus:border-[#5865F2]"
                    placeholder="이미지 안에 표시할 문구를 입력하세요."
                  />
                  <p className="mt-1 flex justify-between text-xs text-gray-600">
                    <span>{"{user} {server} {member_count}"}</span>
                    <span>{form[cardTextKey].length}/180</span>
                  </p>
                </div>
              )}
              {cardBodyMode === "ai" && (
                <div className="mt-3">
                  <textarea
                    value={form[cardAiPromptKey]}
                    onChange={(event) =>
                      set(cardAiPromptKey, event.target.value)
                    }
                    maxLength={500}
                    rows={3}
                    className="w-full resize-y rounded-lg border border-[#404249] bg-[#232428] px-4 py-3 text-sm leading-6 text-white outline-none focus:border-[#5865F2]"
                    placeholder={
                      leaveCard
                        ? "예: 떠나는 멤버에게 한 문장으로 작별 인사를 해 줘."
                        : "예: 새 멤버를 한 문장으로 귀엽게 환영해 줘."
                    }
                  />
                  <p className="mt-1 text-right text-xs text-gray-600">
                    {form[cardAiPromptKey].length}/500
                  </p>
                </div>
              )}
              {cardBodyMode !== "metadata" && (
                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <label
                      htmlFor="card-text-size"
                      className="text-xs font-semibold text-gray-400"
                    >
                      본문 글자 크기
                    </label>
                    <span className="rounded bg-[#2d2f35] px-2 py-1 text-xs font-bold text-[#b9beff]">
                      {form.cardTextSize}px
                    </span>
                  </div>
                  <input
                    id="card-text-size"
                    type="range"
                    min="28"
                    max="42"
                    step="1"
                    value={form.cardTextSize}
                    onChange={(event) =>
                      set("cardTextSize", Number(event.target.value))
                    }
                    className="w-full accent-[#5865F2]"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold text-gray-400">
                글자 폰트
              </label>
              <Dropdown
                value={form.font}
                onChange={(value) => set("font", value)}
                options={FONTS}
                label="폰트 선택"
              />
            </div>
            <div className="flex flex-wrap gap-4">
              {[
                ["showUserId", "사용자 아이디"],
                ["showDiscordJoined", "Discord 가입일"],
                ["showServerJoined", "서버 입장일"],
              ].map(([key, label]) => (
                <Toggle
                  key={key}
                  checked={form[key]}
                  onChange={(value) => set(key, value)}
                  label={label}
                />
              ))}
            </div>
          </div>
        )}
        {form.format !== "card" && <div
          className={`rounded-lg border p-4 ${foodOwned ? "border-[#625bd0]/50 bg-[#5865F2]/8" : "border-[#34363c] bg-[#202125]"}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkles
                className={`h-5 w-5 ${foodOwned ? "text-[#9ea6ff]" : "text-gray-600"}`}
              />
              <div>
                <h4 className="text-sm font-bold text-white">AI 입장문 생성</h4>
                <p className="mt-0.5 text-xs text-gray-500">
                  새 멤버마다 자연스러운 문구를 새로 작성합니다.
                </p>
              </div>
            </div>
            {foodOwned ? (
              <Toggle
                checked={form.aiEnabled}
                onChange={(value) => set("aiEnabled", value)}
                label=""
              />
            ) : (
              <button
                type="button"
                onClick={() => setFoodOpen(true)}
                className="flex items-center gap-1 text-xs font-bold text-[#9ea6ff]"
              >
                <LockKeyhole className="h-3.5 w-3.5" />
                먹이 필요
              </button>
            )}
          </div>
          {foodOwned && form.aiEnabled && (
            <div className="mt-4">
              <textarea
                value={form.aiPrompt}
                onChange={(event) => set("aiPrompt", event.target.value)}
                maxLength={500}
                rows={3}
                className="w-full resize-y rounded-lg border border-[#404249] bg-[#232428] px-4 py-3 text-sm leading-6 text-white outline-none focus:border-[#5865F2]"
                placeholder="예: 새 멤버를 친근하게 환영하고 규칙 채널을 확인해 달라고 디덕이 말투로 안내해 줘."
              />
              <p className="mt-1 text-right text-xs text-gray-600">
                {form.aiPrompt.length}/500
              </p>
            </div>
          )}
        </div>}
        <div>
          <p className="mb-2 text-xs font-bold text-gray-500">4. 저장</p>
          <button
            type="button"
            onClick={save}
            disabled={saving || channels.length === 0}
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-5 font-bold text-white transition-colors hover:bg-[#4752c4] active:scale-[.98] disabled:bg-[#3c3f54] sm:w-auto"
          >
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}설정 저장
          </button>
        </div>
      </div>
      <aside className="border-t border-[#303238] bg-[#18191c] p-5 xl:border-l xl:border-t-0">
        <p className="mb-2 text-xs font-bold text-gray-500">3. 미리보기와 테스트</p>
        <p className="mb-4 text-sm font-bold text-white">{leaveCard ? "퇴장" : "입장"} 메시지 미리보기</p>
        <div className="rounded-lg border border-[#33353b] bg-[#202225] p-3">
          {form.format === "text" ? (
            <div className="flex gap-3">
              <img
                src="/dideok-assistant.png"
                alt=""
                className="h-10 w-10 rounded-full object-contain"
              />
              <div>
                <p className="text-sm font-bold text-white">
                  PartyBot{" "}
                  <span className="rounded bg-[#5865F2] px-1 text-[9px]">
                    BOT
                  </span>
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-gray-200">
                  {activeMessage
                    .replaceAll("{mention}", `@${previewUser}`)
                    .replaceAll(
                      "{server}",
                      selectedServer?.server_name || "서버",
                    )
                    .replaceAll("{member_count}", "128")
                    .replaceAll("{user}", previewUser)}
                </p>
              </div>
            </div>
          ) : form.format === "embed" ? (
            <div className="border-l-4 border-[#5865F2] bg-[#18191c] p-3">
              <p className="mb-3 text-sm leading-5 text-gray-200">
                {activeMessage
                  .replaceAll("{mention}", `@${previewUser}`)
                  .replaceAll("{server}", selectedServer?.server_name || "서버")
                  .replaceAll("{member_count}", "128")
                  .replaceAll("{user}", previewUser)}
              </p>
              <div
                className={`aspect-video w-full overflow-hidden rounded-md ${form.imageSource === "avatar" ? "bg-[#5865F2]" : "bg-[#2b2440]"}`}
              >
                <img
                  src={selectedRepresentative}
                  alt=""
                  className={`h-full w-full ${form.imageSource === "custom" ? "object-cover" : "object-contain"}`}
                />
              </div>
            </div>
          ) : (
            <div className="relative aspect-video overflow-hidden rounded-lg">
              <img
                src={selectedBackground}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div
                className="absolute inset-2 rounded-lg border border-[#8b5cf6] bg-black/35 p-2"
                style={{ fontFamily: FONT_FAMILY[form.font] }}
              >
                <div className="flex items-center gap-2">
                  <img
                    src={selectedRepresentative}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded-full border-2 border-white bg-white object-cover"
                  />
                  <p className="line-clamp-2 text-xs font-black leading-4 text-white">
                    {previewCardTitle}
                  </p>
                </div>
                <p
                  className="mt-1 line-clamp-2 whitespace-pre-wrap font-black text-white"
                  style={{
                    fontSize: `${(form.cardTextSize * 8) / 35}px`,
                    lineHeight: 1.25,
                  }}
                >
                  {previewCardText}
                </p>
                <div className="mt-1 space-y-0.5 text-[8px] leading-3 text-white">
                  {form.showUserId && (
                    <p className="rounded bg-black/65 px-2 py-0.5">
                      아이디 1234567890
                    </p>
                  )}
                  {form.showDiscordJoined && (
                    <p className="rounded bg-black/65 px-2 py-0.5">
                      Discord 가입 2026. 08. 11.
                    </p>
                  )}
                  {form.showServerJoined && (
                    <p className="rounded bg-black/65 px-2 py-0.5">
                      서버 {leaveCard ? "퇴장" : "가입"} 2026. 08. 11.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={sendTest}
          disabled={saving || testing || channels.length === 0}
          className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-[#5865F2] bg-[#5865F2]/10 px-4 text-sm font-bold text-[#c9cdff] transition-colors hover:bg-[#5865F2]/20 active:scale-[.98] disabled:cursor-not-allowed disabled:border-[#3b3d44] disabled:text-gray-600"
        >
          {testing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {testing ? "테스트 전송 중" : "Discord로 테스트 전송"}
        </button>
        <p className="mt-2 text-center text-xs leading-5 text-gray-600">
          현재 설정을 저장한 뒤 선택한 채널로 한 번 전송합니다.
        </p>
      </aside>
      <AnimatePresence>
        {foodOpen && (
          <FoodModal
            serverId={selectedServerId}
            customerName="Disparty User"
            onClose={() => setFoodOpen(false)}
            onPurchased={onFoodOwned}
            setNotice={setNotice}
            localPreview={localPreview}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
